import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFRadio from "@/components/Input/Radio";
import VLFSelectNew from "@/components/Input/SelectNew";

import {PlayerAPI} from "@/api/player";
import {PlayerRoleAPI} from "@/api/player_role";
import {RosterAPI} from "@/api/roster";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	AREA_OPTIONS,
	genderOptions,
	getInternalClubId,
	REQUIRED_FIELD,
	sideOptions,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	statusOptions,
	playerSemaphoreOptions,
} from "@/utils/constants";
import {
	checkRequiredFields,
	getRosterName,
	visibleRosters,
} from "@/utils/functions";

import "./index.scss";

// Documenti accettati in distinta per riconoscere il giocatore
const DOCUMENT_TYPES = [
	{value: "Carta d'identità", label: "Carta d'identità"},
	{value: "Passaporto", label: "Passaporto"},
	{value: "Permesso di soggiorno", label: "Permesso di soggiorno"},
];

const dateValue = (value) => (value ? new Date(value) : null);
const dayOnly = (value) => (value ? moment(value).format("YYYY-MM-DD") : null);

const ASSIGNMENT_OPTIONS = [
	{value: "pool", label: "Aggiungi al pool"},
	{value: "roster", label: "Assegna a una rosa"},
	{value: "external", label: "Esterno (database scouting)"},
];

const HandlePlayerPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const {t} = useTranslation();

	const seasonId = useSelector((state) => state?.season?.id);
	const userId = useSelector((state) => state?.user?.userId);
	const clubId = useSelector((state) => state?.user?.clubId);
	const userName = useSelector((state) => state?.user?.userName);

	const user = useSelector((state) => state?.user);

	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({
		dateOfBirth: null,
		gender: "M",
		status: null,
		assignment: ASSIGNMENT_OPTIONS[0],
		roster: null,
	});
	const [playerRolesList, setPlayerRolesList] = useState([]);
	const [rosters, setRosters] = useState([]);

	const loadPlayerRoles = async () => {
		try {
			const response = await PlayerRoleAPI.list();
			setPlayerRolesList(response || []);
		} catch {
			// silenzioso
		}
	};

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			response.status = {value: response.status, label: response.status};

			if (response.semaphore) {
				response.semaphore = {
					value: response.semaphore,
					label: playerSemaphoreOptions.filter(
						(so) => so.value === response.semaphore,
					)?.[0]?.label,
				};
			}

			// Ripopola primaryRole: cerca in AREA_OPTIONS per label
			const primaryRoleLabel = response.goalkeeper
				? "Portiere"
				: response.primaryRole;
			response.playerPrimaryRole =
				AREA_OPTIONS.find((o) => o.label === primaryRoleLabel) || null;

			// Ripopola secondaryRole: il campo arriva come stringa (nome del ruolo)
			const currentSecondaryRole = playerRolesList.find(
				(o) => o.name === response.secondaryRole,
			);
			response.playerSecondaryRole =
				response.secondaryRole && currentSecondaryRole
					? {value: currentSecondaryRole.id, label: currentSecondaryRole.name}
					: null;

			setFields(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const loadRosters = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setRosters(
				visibleRosters(
					(response || []).filter(
						(r) =>
							!r.prospect && (!r.clubId || r.clubId === getInternalClubId()),
					),
				).map((r) => ({value: r.id, label: getRosterName(r)})),
			);
		} catch {
			setRosters([]);
		}
	};

	useEffect(() => {
		loadPlayerRoles();
	}, []);

	useEffect(() => {
		if (!edit && seasonId) loadRosters();
	}, [edit, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (edit) {
			getPlayer();
		}
	}, [edit, playerRolesList]); // eslint-disable-line react-hooks/exhaustive-deps

	const getRequiredFields = () => {
		if (edit) {
			return ["firstName", "lastName", "status", "playerPrimaryRole"];
		}
		const base = ["firstName", "lastName", "playerPrimaryRole"];
		if (fields?.assignment?.value !== "external") base.push("status");
		if (fields?.assignment?.value === "roster") base.push("roster");
		return base;
	};

	const savePlayer = async () => {
		let errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			setLoading(true);

			let body = Object.assign({}, fields);

			const isGoalkeeper = fields?.playerPrimaryRole?.value === "Goalkeeper";
			body = {
				...body,
				dateOfBirth: fields.dateOfBirth
					? moment(fields.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
				membershipDate: dayOnly(fields.membershipDate),
				identityDocumentExpiry: dayOnly(fields.identityDocumentExpiry),
				fatherHeight: fields?.fatherHeight || 0,
				motherHeight: fields?.motherHeight || 0,
				goalkeeper: isGoalkeeper,
				primaryRole: fields?.playerPrimaryRole?.label || null,
				secondaryRole: isGoalkeeper
					? null
					: fields?.playerSecondaryRole?.label || null,
				clubId,
				userId,
				user: {userId, userName},
				status: fields?.status?.value,
				semaphore: fields?.semaphore?.value,
				playerCategoryId: fields.playerCategoryId ?? null,
			};

			delete body.assignment;
			delete body.roster;

			const assignment = fields?.assignment?.value;
			if (!edit) {
				if (assignment === "roster") {
					body.rosterId = fields?.roster?.value;
				} else if (assignment === "pool") {
					body.addToPool = true;
				} else if (assignment === "external") {
					// Esterno = nessuna rosa e nessun marcatore interno (come lo scouting)
					delete body.status;
					delete body.clubId;
				}
			}

			try {
				const response = edit
					? await PlayerAPI.update(id, body)
					: await PlayerAPI.create(body);
				const createdExternal = !edit && assignment === "external";
				const targetId = response?.id || id;
				setTimeout(() => {
					navigate(
						createdExternal
							? `/scouting/players/${targetId}`
							: `/players/${targetId}`,
					);
					handleNotification(SUCCESSFUL_OPERATION, "success");
					setLoading(false);
				}, 1000);
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		savePlayer();
	};

	const getTextField = (
		fieldKey,
		label,
		required,
		values = fields,
		setValues = setFields,
		type = "text",
	) => {
		return (
			<VLFTextField
				type={type}
				value={values[fieldKey] || ""}
				onChange={(e) => {
					required && setErrors([]);
					setValues({...values, [fieldKey]: e.target.value});
				}}
				label={label}
				placeholder={label}
				required={required}
				size="small"
				helperText={errors.indexOf(fieldKey) !== -1 ? REQUIRED_FIELD : ""}
				error={errors.indexOf(fieldKey) !== -1}
			/>
		);
	};

	const goBack = () => {
		if (edit) {
			navigate("/players/" + id);
		} else {
			navigate("/players");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [
		{
			label: "Giocatori",
			path: "/players",
		},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields
					? (fields.firstName || "") + " " + (fields.lastName || "")
					: "",
				path: "/players/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuovo giocatore"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										{getTextField("firstName", t("name"), true)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("lastName", t("surname"), true)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label={t("birthdate")}
											value={fields.dateOfBirth}
											onChange={(value) => {
												setErrors([]);
												setFields({...fields, dateOfBirth: value});
											}}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("placeOfBirth", t("birthplace"), false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("address", t("address"), false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("nationality", t("nationality"), false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField("email", "Email", false)}
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField(
											"telephone",
											t("phone"),
											false,
											fields,
											setFields,
											"phone",
										)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={statusOptions}
											sorted={false}
											value={fields.status}
											onChange={(newValue) => {
												setFields({
													...fields,
													status: newValue,
												});
											}}
											required
											label={t("condition")}
										/>
									</div>
									{user?.roleName === "Admin" ? (
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={playerSemaphoreOptions}
												value={fields.semaphore}
												onChange={(newValue) => {
													setFields({
														...fields,
														semaphore: newValue,
													});
												}}
												label={t("zone")}
											/>
										</div>
									) : null}
									<div className="col-sm-12 col-md-6">
										<VLFRadio
											name="Genere"
											value={fields.gender || ""}
											onChange={(event) => {
												setFields({...fields, gender: event.target.value});
											}}
											options={genderOptions}
											label={t("gender")}
											size="small"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFRadio
											name="Piede"
											value={fields.favoriteFoot || ""}
											onChange={(event) => {
												setFields({
													...fields,
													favoriteFoot: event.target.value,
												});
											}}
											options={sideOptions(t)}
											label={t("favorite_foot")}
											size="small"
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Tesseramento e documento"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										{getTextField("serialNumber", "Matricola FIGC", false)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label="Data di tesseramento"
											value={dateValue(fields.membershipDate)}
											onChange={(value) =>
												setFields({...fields, membershipDate: value})
											}
											clearable
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={DOCUMENT_TYPES}
											sorted={false}
											value={DOCUMENT_TYPES.find(
												(o) => o.value === fields.identityDocumentType,
											)}
											onChange={(newValue) =>
												setFields({
													...fields,
													identityDocumentType: newValue?.value || null,
												})
											}
											label="Documento d'identità"
										/>
									</div>
									<div className="col-sm-12 col-md-6">
										{getTextField(
											"identityDocumentNumber",
											"Numero documento",
											false,
										)}
									</div>
									<div className="col-sm-12 col-md-6">
										<VLFDatePicker
											label="Scadenza documento"
											value={dateValue(fields.identityDocumentExpiry)}
											onChange={(value) =>
												setFields({...fields, identityDocumentExpiry: value})
											}
											clearable
										/>
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni ruolo"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={AREA_OPTIONS}
											sorted={false}
											value={fields.playerPrimaryRole}
											onChange={(newValue) => {
												setFields({
													...fields,
													playerPrimaryRole: newValue,
													playerSecondaryRole: null,
												});
											}}
											label={t("role")}
											required
											helperText={
												errors.indexOf("playerPrimaryRole") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("playerPrimaryRole") !== -1}
										/>
									</div>
									{fields?.playerPrimaryRole?.value === "Goalkeeper" ? (
										<div className="col-sm-12 col-md-6">
											<VLFRadio
												name="Mano"
												value={fields.favoriteHand || ""}
												onChange={(event) => {
													setFields({
														...fields,
														favoriteHand: event.target.value,
													});
												}}
												options={sideOptions(t)}
												label={t("favorite_hand")}
												size="small"
											/>
										</div>
									) : fields?.playerPrimaryRole ? (
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={playerRolesList.map((r) => ({
													value: r.id,
													label: r.name,
												}))}
												value={fields.playerSecondaryRole || null}
												onChange={(newValue) => {
													setFields({
														...fields,
														playerSecondaryRole: newValue,
													});
												}}
												label="Ruolo specifico"
											/>
										</div>
									) : null}
								</div>
							}
						/>
					</div>
				</div>
				{!edit && (
					<div className="row no-margin mt-2">
						<div className="col-sm-12 padding-small">
							<VLFCard
								title="Assegnazione"
								content={
									<div className="row no-margin">
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={ASSIGNMENT_OPTIONS}
												sorted={false}
												value={fields.assignment}
												onChange={(newValue) => {
													setErrors([]);
													setFields({
														...fields,
														assignment: newValue,
														roster:
															newValue?.value === "roster"
																? fields.roster
																: null,
													});
												}}
												label="Destinazione"
											/>
										</div>
										{fields?.assignment?.value === "roster" ? (
											<div className="col-sm-12 col-md-6">
												<VLFSelectNew
													options={rosters}
													sorted={false}
													value={fields.roster}
													onChange={(newValue) => {
														setErrors([]);
														setFields({...fields, roster: newValue});
													}}
													label="Rosa di destinazione"
													required
													helperText={
														errors.indexOf("roster") !== -1
															? REQUIRED_FIELD
															: ""
													}
													error={errors.indexOf("roster") !== -1}
												/>
											</div>
										) : null}
									</div>
								}
							/>
						</div>
					</div>
				)}
			</div>
		</Page>
	);
};

export default HandlePlayerPage;
